import { useState } from 'react';
import { apiClient } from '../lib/api-client';
import { useAuth } from '../hooks/useAuth';
import { DiscoveryScanProgress } from './DiscoveryScanProgress';
import { ScanFooterLine } from './ScanFooterLine';

const DISCOVERY_SCAN_KEY = 'sdd-maturity-discovery-scan';
const SCAN_TTL_MS = 15 * 60 * 1000; // 15 min — auto-expire stale scans

// Storage is keyed per-team so a scan started under one team never bleeds into
// another team's phase page (the panel is single-team, single-phase by design).
function keyFor(teamNumber: string): string {
  return `${DISCOVERY_SCAN_KEY}:${teamNumber}`;
}

function loadArn(teamNumber: string): string | null {
  try {
    const raw = localStorage.getItem(keyFor(teamNumber));
    if (!raw) return null;
    const envelope = JSON.parse(raw) as { ts: number; data: string };
    if (Date.now() - envelope.ts > SCAN_TTL_MS) {
      localStorage.removeItem(keyFor(teamNumber));
      return null;
    }
    return envelope.data;
  } catch {
    return null;
  }
}

function saveArn(teamNumber: string, arn: string) {
  try { localStorage.setItem(keyFor(teamNumber), JSON.stringify({ ts: Date.now(), data: arn })); } catch { /* noop */ }
}

function clearArn(teamNumber: string) {
  try { localStorage.removeItem(keyFor(teamNumber)); } catch { /* noop */ }
}

/**
 * Inline Discovery scan for one team, rendered at the bottom of the team's
 * Discovery phase page. Replaces the old `/scan` page + Discovery/Delivery
 * toggle — the phase is implied by which page hosts the panel. On a terminal
 * status the panel calls `onScanComplete` so the page can refetch and show the
 * fresh scores in place.
 */
export function DiscoveryScanPanel({
  teamNumber,
  teamName,
  onScanComplete,
  lastScannedAt,
  align,
}: {
  teamNumber: string;
  teamName: string;
  onScanComplete?: () => void;
  /** Most recent scan timestamp across this team's features; shows "Last scanned X ago". */
  lastScannedAt?: string | null;
  /** Alignment of the scan line; 'right' tucks it under the table's bottom-right. */
  align?: 'left' | 'right';
}) {
  const { token } = useAuth();
  const [arn, setArn] = useState<string | null>(() => loadArn(teamNumber));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stays true while polling; set false on terminal status so the button
  // re-enables while the progress panel keeps showing the final state.
  const [running, setRunning] = useState(() => loadArn(teamNumber) !== null);

  async function handleScan() {
    if (!token) {
      setError('Please sign in to trigger a discovery scan');
      return;
    }
    setSubmitting(true);
    setError(null);
    clearArn(teamNumber);
    setArn(null);
    setRunning(true);
    try {
      const result = await apiClient.triggerDiscoveryScan(teamNumber, token, teamName);
      // Opaque poll key — an executionArn once dispatched, or the requestId while
      // the request sits QUEUED and no execution exists yet (AC-5).
      const pollKey = result.executionArn ?? result.requestId;
      if (pollKey) {
        setArn(pollKey);
        saveArn(teamNumber, pollKey);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to trigger discovery scan');
      setRunning(false);
    } finally {
      setSubmitting(false);
    }
  }

  function handleComplete() {
    // Keep storage so the panel re-mounts with the terminal status when the
    // user navigates away and back; the 15-min TTL bounds staleness and a new
    // Scan Now click clears it explicitly.
    setRunning(false);
    onScanComplete?.();
  }

  return (
    <div className="mb-lg" data-testid="discovery-scan-section">
      <ScanFooterLine
        lastScannedAt={lastScannedAt}
        busy={submitting || running}
        busyLabel={submitting ? 'Starting...' : 'Scanning...'}
        onScan={() => { void handleScan(); }}
        testId="start-discovery-scan"
        ariaLabel="Scan Discovery features"
        align={align}
      />
      {error && (
        <p className="badge badge-error mt-md" data-testid="discovery-scan-error">
          Error: {error}
        </p>
      )}
      {arn && (
        <div className="mt-md">
          <DiscoveryScanProgress executionArn={arn} onComplete={handleComplete} />
        </div>
      )}
    </div>
  );
}
